<template>
  <div class="bulk-price-update-modal">
    <NumberInput
      id="bulk-price-percentage"
      label="Percentage (%)"
      type="number"
      v-model="percentageInput"
      :error="error"
      :disabled="loading"
    />

    <p class="bulk-price-update-modal__hint">
      Gebruik een punt als decimaalteken (bijv. 8.8 — niet 8,8).
    </p>

    <div
      v-if="newPrice !== null && newPrice !== currentPrice"
      class="bulk-price-update-modal__preview"
    >
      <span class="bulk-price-update-modal__preview-label">Nieuwe prijs</span>
      <span class="bulk-price-update-modal__preview-value">
        {{ formatPrice(currentPrice) }} →
        <strong>{{ formatPrice(newPrice) }}</strong>
      </span>
    </div>

    <div class="modal__actions">
      <Btn text="Annuleren" variant="outlined" :disabled="loading" @click="handleCancel" />
      <Btn text="Uitvoeren" variant="primary" :loading="loading" @click="handleSubmit" />
    </div>
  </div>
</template>

<script>
  import { ref, computed } from 'vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { useUIStore } from '@/store/uiStore';

  export default {
    name: 'BulkPriceUpdateModal',
    components: { NumberInput, Btn },
    props: {
      modalId: { type: [Number, String], required: true },
      description: { type: String, default: 'Met hoeveel procent wil je de prijs aanpassen?' },
      defaultPercentage: { type: [Number, String], default: 5 },
      currentPrice: { type: Number, default: null },
      loading: { type: Boolean, default: false },
      onConfirm: { type: Function, required: true },
    },
    setup(props) {
      const uiStore = useUIStore();
      const percentageInput = ref(String(props.defaultPercentage));
      const error = ref('');

      const parsedPercentage = computed(() => {
        const raw = String(percentageInput.value).trim();
        if (raw === '') return NaN;
        return Number(raw);
      });

      const newPrice = computed(() => {
        if (props.currentPrice == null || !Number.isFinite(parsedPercentage.value)) return null;
        return Math.round(props.currentPrice * (1 + parsedPercentage.value / 100) * 100) / 100;
      });

      const formatPrice = (value) => {
        if (value == null) return '-';
        return `€${Number(value).toFixed(2).replace('.', ',')}`;
      };

      const handleSubmit = () => {
        if (props.loading) return;
        if (!Number.isFinite(parsedPercentage.value)) {
          error.value = 'Voer een geldig percentage in met een punt als decimaalteken (bijv. 8.8).';
          return;
        }
        error.value = '';
        props.onConfirm(parsedPercentage.value);
      };

      const handleCancel = () => {
        if (props.loading) return;
        uiStore.closeModal(props.modalId);
      };

      return {
        percentageInput,
        error,
        newPrice,
        formatPrice,
        handleSubmit,
        handleCancel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .bulk-price-update-modal {
    &__hint {
      margin-top: 0.5rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__preview {
      margin-top: 1rem;
      padding: 0.75rem 1rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
    }

    &__preview-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-500);
    }

    &__preview-value {
      color: var(--slate-700);

      strong {
        color: var(--slate-900);
      }
    }

    .modal__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 1.5rem;
    }
  }
</style>
